import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const STATUS_LABELS = {
  en_route: 'En route',
  en_cours: 'En cours',
};

const formatRelativeTime = (isoString) => {
  if (!isoString) return '';
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'à l\'instant';
  if (diffMins < 60) return `il y a ${diffMins} min`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `il y a ${diffHours}h`;
  return `il y a ${Math.floor(diffHours / 24)}j`;
};

export default function TechnicianMap({ interventions }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const activeInterventions = interventions.filter(
    i => ['en_route', 'en_cours'].includes(i.status) && i.last_position?.latitude && i.last_position?.longitude
  );

  useEffect(() => {
    if (!mapRef.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        zoomControl: true,
        attributionControl: true,
      }).setView([43.58, 6.93], 10);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 18,
      }).addTo(mapInstanceRef.current);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    markersRef.current.forEach(m => map.removeLayer(m));
    markersRef.current = [];

    if (activeInterventions.length === 0) return;

    const bounds = [];

    activeInterventions.forEach(intervention => {
      const pos = intervention.last_position;
      const lat = pos.latitude;
      const lng = pos.longitude;
      const posTimestamp = pos.timestamp;
      const diffMin = posTimestamp ? Math.floor((Date.now() - new Date(posTimestamp).getTime()) / 60000) : 999;
      const isOld = diffMin > 60;

      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          background: ${isOld ? '#9CA3AF' : '#3DD742'};
          color: white;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
          box-shadow: 0 2px 8px rgba(0,0,0,0.25);
          border: 2px solid white;
          display: flex;
          align-items: center;
          gap: 4px;
        ">
          <span style="font-size:14px;">${isOld ? '⏳' : '🚐'}</span>
          ${intervention.technician_name || 'Technicien'}
        </div>`,
        iconSize: [0, 0],
        iconAnchor: [60, 15],
      });

      const marker = L.marker([lat, lng], { icon }).addTo(map);

      const popupContent = `
        <div style="font-family: system-ui; min-width: 180px;">
          <div style="font-weight: 700; font-size: 14px; margin-bottom: 6px;">${intervention.technician_name || 'Technicien'}</div>
          <div style="font-size: 12px; color: #555; margin-bottom: 4px;">
            <span style="background: ${isOld ? '#F3F4F6' : '#ECFDF5'}; color: ${isOld ? '#6B7280' : '#059669'}; padding: 2px 8px; border-radius: 10px; font-weight: 500;">
              ${isOld ? 'Position ancienne' : STATUS_LABELS[intervention.status] || intervention.status}
            </span>
          </div>
          <div style="font-size: 13px; font-weight: 500; margin: 8px 0 4px;">Client: ${intervention.client_name}</div>
          <div style="font-size: 12px; color: #666;">${pos.address || 'Adresse non disponible'}</div>
          <div style="font-size: 11px; color: #999; margin-top: 6px;">${formatRelativeTime(posTimestamp)}</div>
        </div>
      `;
      marker.bindPopup(popupContent);
      markersRef.current.push(marker);
      bounds.push([lat, lng]);
    });

    if (bounds.length === 1) {
      map.setView(bounds[0], 14);
    } else if (bounds.length > 1) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, [activeInterventions]);

  if (activeInterventions.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-400 text-sm">
        Aucun technicien en intervention actuellement
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" style={{ position: 'relative', zIndex: 1 }}>
      <div
        ref={mapRef}
        style={{ height: '320px', width: '100%', zIndex: 1 }}
        data-testid="technician-map"
      />
    </div>
  );
}
